import { useEffect } from 'react'
import { Capacitor } from '@capacitor/core'
import { PushNotifications, type Token, type ActionPerformed, type PushNotificationSchema } from '@capacitor/push-notifications'
import { Device } from '@capacitor/device'
import { useAuth } from 'headlo-auth'
import { useDpopFetch } from './useDpopFetch'

// usePushNotifications — one-shot boot hook wired into main.tsx. Handles
// the full push lifecycle: permission request → OS token → register with
// worker → listen for foreground pushes → listen for tap actions.
//
// Only fires on native platforms. Web dev (running in a browser via
// npm run dev) has no push subsystem — the hook is a no-op there.
//
// Timing: called after sign-in completes. Runs once per sign-in state
// change. Re-registers on every launch so the last_seen_at column stays
// fresh — the mobile_push_token cleanup job (deferred) uses that to
// prune abandoned devices.

const API_URL = (import.meta.env.VITE_HEADLO_API_URL as string | undefined) ?? 'https://api.headlo.com'

export interface PushHandlers {
  /**
   * Called when a push arrives while the app is in the foreground. iOS
   * suppresses the OS notification UI in this state, so the app should
   * either show its own in-app banner OR silently refresh the affected
   * data. Default: silent refresh (up to the caller to invalidate
   * relevant contexts).
   */
  onForeground?: (payload: PushNotificationSchema) => void

  /**
   * Called when the user taps a notification (from lock screen or notification
   * center). Payload includes the `data` field from the server-side send —
   * use it to route to the right screen (e.g. order detail).
   */
  onTap?: (payload: ActionPerformed) => void
}

export function usePushNotifications(handlers?: PushHandlers) {
  const { isLoaded, isSignedIn } = useAuth()
  const authFetch = useDpopFetch()

  useEffect(() => {
    if (!isLoaded || !isSignedIn) return
    if (!Capacitor.isNativePlatform()) {
      // Web dev — nothing to do. Log once so the absence isn't mysterious
      // when debugging why pushes don't arrive during `npm run dev`.
      console.log('[push] skipping — non-native platform')
      return
    }

    let cleanup: (() => void) | null = null

    void (async () => {
      // Step 1: check current permission. iOS returns 'prompt' on first
      // install; Android returns 'granted' by default on API < 33 (before
      // Google added runtime permission for notifications).
      let perms = await PushNotifications.checkPermissions()
      if (perms.receive !== 'granted') {
        perms = await PushNotifications.requestPermissions()
        if (perms.receive !== 'granted') {
          console.warn('[push] permission denied — no notifications will be delivered')
          return
        }
      }

      // Step 2: register with APNs (iOS) or FCM (Android). Fires the
      // 'registration' event asynchronously — we subscribe first, then call.
      const registrationListener = await PushNotifications.addListener('registration', async (token: Token) => {
        try {
          const device = await Device.getId()
          const info   = await Device.getInfo()
          const res = await authFetch(`${API_URL.replace(/\/$/, '')}/v1/mobile/push/register`, {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify({
              token:           token.value,
              platform:        info.platform === 'ios' ? 'ios' : 'android',
              installation_id: device.identifier,
              app_version:     '0.1.0',   // TODO: read from app manifest when we wire electron-builder-style versioning
            }),
          })
          if (!res.ok) console.error(`[push] register failed: ${res.status} ${await res.text().catch(() => '')}`)
          else         console.log('[push] token registered')
        } catch (e) {
          console.error('[push] token register error:', e)
        }
      })

      const errorListener = await PushNotifications.addListener('registrationError', err => {
        console.error('[push] registration error:', err)
      })

      const receivedListener = await PushNotifications.addListener(
        'pushNotificationReceived',
        (payload: PushNotificationSchema) => {
          // Foreground receipt — iOS suppresses the OS UI here by default.
          // Caller decides whether to show an in-app banner, silently
          // refresh, or both.
          handlers?.onForeground?.(payload)
        },
      )

      const actionListener = await PushNotifications.addListener(
        'pushNotificationActionPerformed',
        (payload: ActionPerformed) => {
          // Tap-to-open — payload.notification.data has the routing hints
          // the server set (route, handle, order_id, source).
          handlers?.onTap?.(payload)
        },
      )

      // Actually kick off the registration — must be after subscribing
      // to `registration` above, otherwise a fast round-trip could
      // deliver the token before our listener attaches.
      await PushNotifications.register()

      cleanup = () => {
        void registrationListener.remove()
        void errorListener.remove()
        void receivedListener.remove()
        void actionListener.remove()
      }
    })()

    return () => { cleanup?.() }
  }, [isLoaded, isSignedIn, authFetch, handlers])
}
